import { formatCurrency } from '../../../utils/formatCurrency';
import dayjs from 'dayjs';
import { offers } from '@prisma/client';
import { OfferRoundWithRelations } from '../../../api/services/offerRounds';

/**
 * Gets the current offer terms by using the latest round's actual column values.
 * The round stores all negotiation terms as columns — these are the source of truth.
 * changedFields is only used for highlighting, not for values.
 */
export const getCurrentTerms = (
  offer: offers,
  latestRound: OfferRoundWithRelations | null
) => {
  if (!latestRound) return offer;

  return {
    ...offer,
    price: latestRound.price,
    closingDate: latestRound.closingDate,
    earnestMoney: latestRound.earnestMoney,
    concessions: latestRound.concessions,
    appliances: latestRound.appliances,
    inspectionPeriod: latestRound.inspectionPeriod,
    contingent: latestRound.contingent,
    bagentComm: latestRound.bagentComm,
    loan: latestRound.loan,
    homeWarrantyType: latestRound.homeWarrantyType,
    termiteInspection: latestRound.termiteInspection,
    termiteBond: latestRound.termiteBond,
    sewerInspection: latestRound.sewerInspection,
    newSurvey: latestRound.newSurvey,
    additionalTerms: latestRound.additionalTerms,
    paymentMethod: latestRound.paymentMethod,
  };
};

/**
 * Gets the list of fields that have been changed in the latest round
 */
export const getChangedFields = (
  latestRound: OfferRoundWithRelations | null
): string[] => {
  return latestRound?.changedFields
    ? Object.keys(latestRound.changedFields as Record<string, unknown>)
    : [];
};

/**
 * Checks if a specific field has been modified in negotiations
 */
export const isFieldChanged = (
  fieldName: string,
  changedFields: string[]
): boolean => {
  return changedFields.includes(fieldName);
};

/**
 * Formats a field value for display based on the field type
 */
export const formatFieldValue = (fieldName: string, value: unknown): string => {
  switch (fieldName) {
    case 'price':
    case 'earnestMoney':
    case 'concessions':
    case 'loan':
      return formatCurrency((value as number) || 0);
    case 'closingDate':
      return dayjs.utc(value as string).format('MM/DD/YYYY');
    case 'bagentComm':
      return `${value}%`;
    case 'inspectionPeriod':
      return `${value} days`;
    case 'contingent':
    case 'appliances':
    case 'termiteInspection':
    case 'termiteBond':
    case 'sewerInspection':
    case 'newSurvey':
      return value ? 'Yes' : 'No';
    case 'homeWarrantyType':
      return value === 'NONE'
        ? 'None'
        : value === 'SELLER'
        ? 'Seller'
        : 'Buyer';
    default:
      return String(value || 'None');
  }
};

/**
 * Gets the display value for commission based on view context
 */
export const getCommissionDisplay = (
  isPublicView: boolean,
  closingCosts: { bac?: number } | null,
  currentTerms: { bagentComm?: number; customCommission?: number | null },
  brokerFee?: number | null
): string => {
  // Use custom commission if set (null = use default, 0 = show $0)
  if (isPublicView && currentTerms.customCommission != null) {
    return formatCurrency(currentTerms.customCommission);
  }
  // Use broker fee from buyer-closing-costs if available (accounts for showings)
  if (isPublicView && brokerFee) {
    return formatCurrency(brokerFee);
  }
  // Fallback to BAC from closing costs
  if (isPublicView && closingCosts?.bac) {
    return formatCurrency(closingCosts.bac);
  }
  return `${currentTerms.bagentComm || 0}%`;
};

/**
 * Gets the commission label based on view context
 */
export const getCommissionLabel = (isPublicView: boolean): string => {
  return isPublicView ? 'Homepoint Commission' : "Buyer's Commissions";
};

/**
 * Determines if offer status section should be shown
 */
export const shouldShowOfferStatus = (isPublicView: boolean): boolean => {
  return !isPublicView;
};

/**
 * Gets status display information
 */
export const getStatusDisplay = (
  status: string
): {
  text: string;
  color: string;
} => {
  const statusMap: Record<string, { text: string; color: string }> = {
    REVIEW: { text: 'Under Review', color: 'warning' },
    PENDING: { text: 'Pending', color: 'info' },
    ACCEPTED: { text: 'Accepted', color: 'success' },
    REJECTED: { text: 'Rejected', color: 'error' },
    EXPIRED: { text: 'Expired', color: 'default' },
    COUNTERED: { text: 'Countered', color: 'warning' },
  };

  return statusMap[status] || { text: status, color: 'default' };
};
